import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  createManagedProducer, getManagedProducers,
  resetManagedProducerPassword, updateManagedProducerStatus, deleteManagedProducer,
  isAdminApiUnavailable, extractApiErrorMessage,
} from '../../../api/adminApi';

/**
 * 승격을 마친 계정인지.
 *
 * 이 목록은 producer_profile.managed_by_admin(어드민이 만들었다는 이력)으로 조회되는데,
 * 승격 후에도 그 이력은 남으므로 일반 계정이 된 회사도 함께 잡힌다.
 * 지금 상태는 accountType이 말해준다. 값이 아직 안 오는 동안에는 대행으로 본다.
 */
export const isUpgraded = (acc) => Boolean(acc?.accountType) && acc.accountType !== 'MANAGED';

/**
 * 지울 수 없는 계정인지, 그 이유.
 *
 * 삭제는 잘못 만든 계정을 치우는 기능이다. 쓰고 있는 계정을 지우면
 * 그 제작사가 로그인하지 못하고, 공고에 딸린 지원자까지 함께 사라진다.
 * 서버도 막지만, 눌러서 실패를 보기 전에 이유를 알려준다.
 *
 * @returns {string|null} 막는 이유. 지울 수 있으면 null
 */
export function deleteBlockReason(acc) {
  if (isUpgraded(acc)) return '정식 계정으로 전환된 계정입니다. 실제 사용자가 쓰는 계정이라 삭제할 수 없습니다.';
  const postings = acc?.postingCount ?? 0;
  if (postings > 0) return `공고 ${postings}건이 남아 있습니다. 먼저 다른 제작사로 이관한 뒤 삭제해주세요.`;
  return null;
}

/** 막지는 않지만 지우기 전에 알려야 하는 것 */
export function deleteWarning(acc) {
  if (acc?.firstLoginAt) return '이 계정으로 로그인한 적이 있습니다. 쓰고 있는 계정이라면 삭제 대신 정지를 권합니다.';
  return null;
}

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

/** 발급된 임시 비밀번호 — 이 화면을 벗어나면 다시 볼 수 없다 */
function IssueCard({ issue, onClose, onCreateCasting }) {
  const [copied, setCopied] = useState(null);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const both = `아이디: ${issue.email}\n임시 비밀번호: ${issue.tempPassword}`;

  return (
    <div className="ag-card" style={{ marginBottom: 12, borderColor: 'var(--color-danger, #EF4444)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {issue.companyName ? `${issue.companyName} ` : ''}계정이 발급되었습니다
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>
            <i className="ti ti-alert-triangle" /> 임시 비밀번호는 지금만 확인할 수 있습니다. 창을 닫으면 다시 볼 수 없습니다.
          </div>
        </div>
        <button className="ag-btn ag-btn--sm" onClick={onClose}>닫기</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        {[
          { label: '아이디', value: issue.email, key: 'id' },
          { label: '임시 비밀번호', value: issue.tempPassword, key: 'pw' },
        ].map(({ label, value, key }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 96, fontSize: 12, color: 'var(--color-text-tertiary)' }}>{label}</span>
            <code style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              background: 'var(--color-background)', border: '1px solid var(--color-border)',
              fontSize: 14, letterSpacing: '0.02em', wordBreak: 'break-all',
            }}>
              {value}
            </code>
            <button className="ag-btn ag-btn--sm" onClick={() => copy(value, key)}>
              {copied === key ? '복사됨' : '복사'}
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="ag-btn ag-btn--primary" onClick={() => copy(both, 'both')}>
          <i className="ti ti-copy" /> {copied === 'both' ? '복사됨!' : '아이디·비밀번호 함께 복사'}
        </button>
        {issue.producerProfileId != null && onCreateCasting && (
          <button className="ag-btn" onClick={() => onCreateCasting(issue)}>
            <i className="ti ti-clipboard-plus" /> 이어서 공고 등록
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 삭제 확인 — 제작사명을 직접 입력해야 지워진다.
 *
 * 되돌릴 수 없고 목록의 행이 서로 비슷해 보이므로, 확인 버튼 한 번으로는
 * 옆 줄을 지우는 사고를 막지 못한다.
 */
function DeleteDialog({ account, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const expected = (account.companyName ?? account.email ?? '').trim();
  const matched = typed.trim() === expected && expected !== '';
  const warning = deleteWarning(account);

  return (
    <div className="ag-card" style={{ marginBottom: 12, borderColor: 'var(--color-danger, #EF4444)' }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>
        {expected} 계정을 삭제합니다
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8, lineHeight: 1.7 }}>
        계정과 제작사 정보가 함께 지워지고 <b>되돌릴 수 없습니다.</b> 이 아이디({account.email ?? '—'})로는
        더 이상 로그인할 수 없습니다.
      </p>
      {warning && (
        <p style={{ fontSize: 13, color: 'var(--color-danger)', marginTop: 4 }}>
          <i className="ti ti-alert-triangle" /> {warning}
        </p>
      )}

      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', margin: '14px 0 6px' }}>
        확인을 위해 <b>{expected}</b> 를 입력해주세요
      </label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          className="ag-field__input"
          value={typed}
          autoFocus
          onChange={(e) => setTyped(e.target.value)}
          placeholder={expected}
          style={{ flex: 1, minWidth: 200 }}
        />
        <button className="ag-btn" onClick={onCancel} disabled={busy}>취소</button>
        <button
          className="ag-btn ag-btn--danger"
          disabled={!matched || busy}
          onClick={() => onConfirm(account)}
        >
          <i className="ti ti-trash" /> {busy ? '삭제 중...' : '영구 삭제'}
        </button>
      </div>
    </div>
  );
}

function CreateForm({ onCreated, onError }) {
  const [form, setForm] = useState({ companyName: '', contactName: '', email: '' });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  // 제작사명과 이메일만 필수다. 담당자는 선택이며 표시용으로만 쓰인다.
  const filled = ['companyName', 'email'].every(k => form[k].trim());
  const canSubmit = filled && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      // 연락처는 보내지 않는다. 대행 계정은 이메일로만 로그인하고,
      // 번호를 넣으면 그 번호를 쓰는 기존 사용자의 휴대폰 로그인이 막힐 수 있다.
      const issued = await createManagedProducer({
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        ...(form.contactName.trim() ? { contactName: form.contactName.trim() } : {}),
      });
      onCreated({ ...issued, companyName: form.companyName.trim() });
      setForm({ companyName: '', contactName: '', email: '' });
    } catch (err) {
      const code = err.response?.status;
      const errorCode = err.response?.data?.code;

      // 중복 이메일은 409가 아니라 400 + code 로 온다
      if (errorCode === 'EMAIL_ALREADY_EXISTS' || code === 409) {
        onError('이미 사용 중인 이메일입니다. 다른 이메일로 발급해주세요.');
        return;
      }

      // 그 밖의 실패는 서버가 보낸 사유를 그대로 보여준다.
      // 400은 원인이 제각각이라 상태 코드만으로는 어디를 고쳐야 할지 알 수 없다.
      const detail = extractApiErrorMessage(err);
      onError(
        detail
          ? (code ? `${detail} (HTTP ${code})` : detail)
          : code
            ? `계정 생성에 실패했습니다. (HTTP ${code})`
            : (err.message || '계정 생성에 실패했습니다.')
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, placeholder, type = 'text') => (
    <div style={{ flex: 1, minWidth: 180 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
        {label}
      </label>
      <input
        className="ag-field__input"
        type={type}
        value={form[key]}
        onChange={set(key)}
        placeholder={placeholder}
        style={{ width: '100%' }}
      />
    </div>
  );

  return (
    <form className="ag-card" style={{ marginBottom: 12 }} onSubmit={submit}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>대행 계정 발급</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {field('제작사명 *', 'companyName', '예: 캐스타 필름')}
        {field('담당자 (선택)', 'contactName', '예: 김담당')}
        {field('아이디(이메일) *', 'email', 'contact@company.com', 'email')}
        <button className="ag-btn ag-btn--primary" type="submit" disabled={!canSubmit} style={{ height: 38 }}>
          <i className="ti ti-user-plus" /> {saving ? '발급 중...' : '계정 발급'}
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 10 }}>
        제작사명과 이메일만 있으면 발급됩니다. 대행 계정은 이메일로만 로그인하므로 연락처는 받지 않습니다.<br />
        발급된 계정은 <b>공고·지원자 관리</b>와 계정 설정만 사용할 수 있으며, 최초 로그인 시 비밀번호를 변경해야 합니다.
      </p>
    </form>
  );
}

export default function AdminManagedProducersPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [issue, setIssue] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [deleting, setDeleting] = useState(null);   // 삭제 확인 중인 계정

  const LIMIT = 20;
  const reqIdRef = useRef(0);

  const fetchAccounts = useCallback(async (p) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await getManagedProducers({ page: p, limit: LIMIT });
      if (myReq !== reqIdRef.current) return;
      setAccounts(data.accounts);
      setTotal(data.total);
      setUnavailable(false);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      if (isAdminApiUnavailable(err)) { setUnavailable(true); setAccounts([]); setTotal(0); return; }
      const code = err.response?.status;
      setError(code ? `목록을 불러오지 못했습니다. (HTTP ${code})` : (err.message || '목록을 불러오지 못했습니다.'));
      setAccounts([]);
      setTotal(0);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(page); }, [page, fetchAccounts]);

  const handleReset = async (acc) => {
    // 이미 정식 계정이 된 회사에 임시 비밀번호를 걸면 실제 고객이 로그인하지 못한다
    if (isUpgraded(acc)) return;
    if (!window.confirm(`${acc.companyName} 계정의 임시 비밀번호를 재발급할까요?\n기존 비밀번호는 즉시 사용할 수 없게 됩니다.`)) return;
    setBusyId(acc.userId);
    try {
      const res = await resetManagedProducerPassword(acc.userId);
      setIssue({ ...res, email: acc.email, companyName: acc.companyName });
      fetchAccounts(page);
    } catch (err) {
      setError(`재발급에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setBusyId(null);
    }
  };

  const handleStatus = async (acc) => {
    const next = acc.status === 'suspended' ? 'active' : 'suspended';
    if (next === 'suspended' && !window.confirm(`${acc.companyName} 계정을 정지할까요?`)) return;
    setBusyId(acc.userId);
    setAccounts(prev => prev.map(a => (a.userId === acc.userId ? { ...a, status: next } : a)));
    try {
      await updateManagedProducerStatus(acc.userId, next);
    } catch (err) {
      setAccounts(prev => prev.map(a => (a.userId === acc.userId ? { ...a, status: acc.status } : a)));
      setError(`상태 변경에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (acc) => {
    if (deleteBlockReason(acc)) return;
    setBusyId(acc.userId);
    setError(null);
    try {
      await deleteManagedProducer(acc.userId);
      setDeleting(null);
      // 페이지의 마지막 한 건을 지웠으면 빈 페이지가 남는다
      const lastOnPage = accounts.length === 1 && page > 1;
      if (lastOnPage) setPage(p => p - 1);
      else fetchAccounts(page);
    } catch (err) {
      const code = err.response?.status;
      if (code === 404) {
        // 계정이 이미 없거나, 삭제 API가 아직 배포되지 않았거나 — 응답만으로는 구분되지 않는다
        setError('계정을 찾을 수 없습니다. 이미 삭제됐거나, 삭제 기능이 아직 배포되지 않았을 수 있습니다.');
        fetchAccounts(page);
      } else if (isAdminApiUnavailable(err)) {
        setError('삭제 기능이 아직 배포되지 않았습니다.');
      } else {
        // 409(공고가 남음) 등 서버가 막은 이유는 그대로 보여준다
        const detail = extractApiErrorMessage(err);
        setError(detail || `삭제에 실패했습니다. (HTTP ${code ?? '네트워크'})`);
      }
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout pageId="managed-producers">
      <div className="admin-topbar">
        <h1>대행 제작사 계정</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>총 {total.toLocaleString()}개</span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      {issue && (
        <IssueCard
          issue={issue}
          onClose={() => setIssue(null)}
          onCreateCasting={(iss) => navigate(
            `/admin/managed-producers/${iss.producerProfileId}/casting?company=${encodeURIComponent(iss.companyName ?? '')}`
          )}
        />
      )}

      {deleting && (
        <DeleteDialog
          account={deleting}
          busy={busyId === deleting.userId}
          onCancel={() => setDeleting(null)}
          onConfirm={handleDelete}
        />
      )}

      <CreateForm onCreated={setIssue} onError={setError} />

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {unavailable && (
        <div className="ag-card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13 }}>
            <i className="ti ti-info-circle" /> 대행 계정 API가 아직 배포되지 않았습니다. 배포 후 목록이 표시됩니다.
          </div>
        </div>
      )}

      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ag-table">
            <thead>
              <tr>
                {['제작사', '아이디', '공고', '최초 로그인', '비번 변경', '상태', '관리'].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="ag-table__empty">불러오는 중...</td></tr>
              ) : accounts.length === 0 ? (
                <tr><td colSpan={7} className="ag-table__empty">발급된 대행 계정이 없습니다</td></tr>
              ) : accounts.map(acc => (
                <tr key={acc.userId}>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{acc.companyName ?? '—'}</span>
                      {isUpgraded(acc) && (
                        <span className="ag-badge ag-badge--gray" title="정식 계정으로 전환됨">승격됨</span>
                      )}
                    </div>
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{acc.email ?? '—'}</td>
                  <td style={{ color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {acc.postingCount ?? 0}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>
                    {acc.firstLoginAt
                      ? fmtDate(acc.firstLoginAt)
                      : <span className="ag-badge ag-badge--warn">미접속</span>}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>
                    {acc.passwordChangedAt
                      ? fmtDate(acc.passwordChangedAt)
                      : <span className="ag-badge ag-badge--gray">임시 비번</span>}
                  </td>
                  <td>
                    <span className={`ag-badge ${acc.status === 'suspended' ? 'ag-badge--danger' : 'ag-badge--success'}`}>
                      {acc.status === 'suspended' ? '정지' : '활성'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="ag-btn ag-btn--sm"
                        disabled={acc.producerProfileId == null}
                        title={acc.producerProfileId == null ? '제작사 프로필 ID가 없습니다' : undefined}
                        onClick={() => navigate(
                          `/admin/managed-producers/${acc.producerProfileId}/casting?company=${encodeURIComponent(acc.companyName ?? '')}`
                        )}
                      >
                        <i className="ti ti-clipboard-plus" /> 공고
                      </button>
                      <button
                        className="ag-btn ag-btn--sm"
                        disabled={busyId === acc.userId || isUpgraded(acc)}
                        title={isUpgraded(acc)
                          ? '정식 계정으로 전환되어 임시 비밀번호를 발급할 수 없습니다'
                          : undefined}
                        onClick={() => handleReset(acc)}
                      >
                        <i className="ti ti-key" /> 재발급
                      </button>
                      <button
                        className={`ag-btn ag-btn--sm${acc.status === 'suspended' ? '' : ' ag-btn--danger'}`}
                        disabled={busyId === acc.userId}
                        onClick={() => handleStatus(acc)}
                      >
                        {acc.status === 'suspended' ? '해제' : '정지'}
                      </button>
                      <button
                        className="ag-btn ag-btn--sm ag-btn--danger"
                        disabled={busyId === acc.userId || Boolean(deleteBlockReason(acc))}
                        title={deleteBlockReason(acc) ?? '계정을 영구 삭제합니다'}
                        onClick={() => { setError(null); setDeleting(acc); }}
                      >
                        <i className="ti ti-trash" /> 삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="ag-pagination">
          <button className="ag-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <i className="ti ti-chevron-left" />
          </button>
          <span style={{ fontSize: 12, padding: '0 10px', color: 'var(--color-text-tertiary)' }}>
            {page} / {totalPages}
          </span>
          <button className="ag-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
